// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ItemDetail } from '@/items/api/types';
import { BOMComparisonRow } from '@/items/components/merge-item/components/bom-comparison-row';
import { FieldComparisonRow } from '@/items/components/merge-item/components/field-comparison-row';
import { ItemFieldsRow } from '@/items/components/merge-item/components/item-fields-row';
import {
  getMergedFieldValue,
  shouldShowFieldRadio,
} from '@/items/components/merge-item/utilities/merge-utilities';
import {
  Alert,
  AlertTitle,
  Box,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

interface Props {
  primaryItem: ItemDetail;
  selectedItem: ItemDetail;
  fieldSelections: Record<string, 'primary' | 'selected'>;
  onFieldSelectionChange: (
    field: string,
    source: 'primary' | 'selected'
  ) => void;
}

const itemFields = [
  { key: 'name', label: 'Item name*' },
  { key: 'sku', label: 'SKU' },
];

export function FieldComparisonTable(props: Props) {
  return (
    <Stack spacing={3}>
      {props.selectedItem &&
        props.selectedItem.type === 'SERVICE' &&
        props.primaryItem.type !== 'SERVICE' && (
          <Alert severity="warning">
            <AlertTitle>Item type change</AlertTitle>
            The selected item "{props.selectedItem.name}" is currently a service
            item. After merging into "{props.primaryItem.name}" (a{' '}
            {props.primaryItem.type.toLowerCase()} item), it will become a{' '}
            {props.primaryItem.type.toLowerCase()} item.
          </Alert>
        )}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {/* Header Row */}
        <Box
          display="grid"
          gridTemplateColumns="2fr 2fr 2fr 3fr"
          sx={{
            backgroundColor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="subtitle2" fontWeight="medium">
            Field
          </Typography>
          <Typography variant="subtitle2" fontWeight="medium">
            {props.primaryItem.name}
          </Typography>
          <Typography variant="subtitle2" fontWeight="medium">
            {props.selectedItem.name}
          </Typography>
          <Typography variant="subtitle2" fontWeight="medium">
            Merged result
          </Typography>
        </Box>

        {/* Data Rows */}
        {itemFields.map((field) => {
          const primaryValue = props.primaryItem[
            field.key as keyof ItemDetail
          ] as string | null;
          const selectedValue = props.selectedItem[
            field.key as keyof ItemDetail
          ] as string | null;
          const mergedValue = getMergedFieldValue(
            field.key,
            props.primaryItem,
            props.selectedItem,
            props.fieldSelections
          );
          const showRadio = shouldShowFieldRadio(
            field.key,
            props.primaryItem,
            props.selectedItem
          );
          const selection = props.fieldSelections[field.key] || 'primary';

          return (
            <FieldComparisonRow
              key={field.key}
              fieldKey={field.key}
              fieldLabel={field.label}
              primaryValue={primaryValue}
              selectedValue={selectedValue}
              mergedValue={mergedValue}
              showRadio={showRadio}
              selection={selection}
              onSelectionChange={props.onFieldSelectionChange}
            />
          );
        })}

        {/* Item Type Row */}
        <Box
          display="grid"
          gridTemplateColumns="2fr 2fr 2fr 3fr"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 2,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">Item type</Typography>
          <Box sx={{ ml: 4 }}>
            <Typography variant="body2">
              {props.primaryItem.type === 'SERVICE'
                ? 'Service'
                : props.primaryItem.type === 'DEVICE'
                  ? 'Device'
                  : 'Bulk'}
            </Typography>
          </Box>
          <Box sx={{ ml: 4 }}>
            <Typography variant="body2">
              {props.selectedItem.type === 'SERVICE'
                ? 'Service'
                : props.selectedItem.type === 'DEVICE'
                  ? 'Device'
                  : 'Bulk'}
            </Typography>
          </Box>
          <Box>
            {props.primaryItem.type !== props.selectedItem.type ? (
              <Tooltip
                arrow
                title="Merged items will use the retained item's type"
                placement="top"
              >
                <Typography variant="body2" sx={{ cursor: 'help' }}>
                  {props.primaryItem.type === 'SERVICE'
                    ? 'Service'
                    : props.primaryItem.type === 'DEVICE'
                      ? 'Device'
                      : 'Bulk'}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="body2">
                {props.primaryItem.type === 'SERVICE'
                  ? 'Service'
                  : props.primaryItem.type === 'DEVICE'
                    ? 'Device'
                    : 'Bulk'}
              </Typography>
            )}
          </Box>
        </Box>

        {/* BOM Row */}
        <BOMComparisonRow
          primaryItem={props.primaryItem}
          selectedItem={props.selectedItem}
          selection={props.fieldSelections.bom || 'primary'}
          onSelectionChange={props.onFieldSelectionChange}
        />

        {/* Item Fields Row */}
        <ItemFieldsRow
          primaryFields={props.primaryItem.fields}
          selectedFields={props.selectedItem.fields}
        />
      </Box>
    </Stack>
  );
}
